import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, Output, SimpleChanges } from "@angular/core";
import { Router } from "@angular/router";
import { MessageService } from "primeng/api";
import { TranslocoService } from "@ngneat/transloco";
import {
  SocialCommentResponse,
  SocialPostResponse,
} from "src/app/model/social/social.types";
import { SocialService } from "src/app/service/social.service";
import { ImageUrlService } from "src/app/service/image-url.service";
import { resolveMediaAspectRatio, SocialMediaContext } from "src/app/model/social/social-media-ratios";
import { enviroment } from "src/environments/environment";

/** Comentário top-level envelopado com estado de UI para thread (replies lazy). */
interface CommentWithReplies extends SocialCommentResponse {
  replies: SocialCommentResponse[];
  repliesLoaded: boolean;
  repliesLoading: boolean;
  repliesExpanded: boolean;
  isReplying: boolean;
  replyBody: string;
  replySending: boolean;
}

/**
 * Superfície mínima usada por onVideoTap/onVideoDblTap: tanto o `<video>` nativo (flag
 * HLS_PLAYBACK desligada) quanto HlsVideoPlayerComponent (via `exportAs`, flag ligada)
 * satisfazem este formato — permite manter a mesma camada de toque (clique = play/pause,
 * duplo = curtir) nos dois caminhos sem o card conhecer o `<video>` interno do player novo.
 */
interface TappableVideo {
  readonly paused: boolean;
  play(): Promise<void>;
  pause(): void;
}

@Component({
  selector: "app-social-post-card",
  templateUrl: "./social-post-card.component.html",
  styleUrls: ["./social-post-card.component.css"],
})
export class SocialPostCardComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() post!: SocialPostResponse;
  @Input() currentUserId?: string;
  @Input() currentUserAvatarUrl: string | null = null;
  /** IDs de projetos já seguidos pelo viewer — usado para esconder o botão "Seguir". */
  @Input() followedProjectIds: Set<string> = new Set<string>();
  /** Contexto de exibição — define a razão padronizada da mídia (feed x detalhe). */
  @Input() context: SocialMediaContext = 'feed';
  @Output() postChanged = new EventEmitter<void>();
  @Output() reportPost = new EventEmitter<string>();
  /** Emitido ao denunciar um comentário/reply (payload = commentId). */
  @Output() reportComment = new EventEmitter<string>();
  /** Emitido ao denunciar a CONTA do autor de um comentário/reply (payload = authorUserId). */
  @Output() reportUser = new EventEmitter<string>();
  /** Emitido após o viewer seguir o projeto a partir do card. */
  @Output() projectFollowed = new EventEmitter<string>();

  likedLocal = false;
  likeBusy = false;
  currentSlide = 0;
  mediaAspectRatio: string = '1 / 1';
  private touchStartX = 0;
  private touchStartY = 0;
  /** Timestamp do último toque curto — detecta double-tap (mobile). */
  private lastTapTime = 0;
  /** Timestamp do último burst do coração — coalesce touchend + dblclick sintético. */
  private lastLikeBurst = 0;
  /** Double-tap enquanto uma curtida/descurtida estava em voo: aplica ao terminar. */
  private pendingDoubleTapLike = false;
  private heartTimer: ReturnType<typeof setTimeout> | null = null;
  /** Exibe a animação do coração ao curtir por double-tap/click. */
  showHeart = false;

  /** Limite de caracteres para comentários e replies. */
  readonly commentMaxLength = 300;

  showComments = false;
  comments: CommentWithReplies[] = [];
  commentsLoading = false;
  newComment = "";
  commentSending = false;
  showActions = false;
  followProjectBusy = false;

  /**
   * Fecha o menu "..." ao clicar fora dele. O botão e o dropdown são irmãos no template, então o
   * teste é por seletor: clique neles não conta como "fora" (o botão alterna, os itens já fecham).
   * Como cada card escuta o documento, abrir o menu de um card fecha o dos outros.
   */
  @HostListener("document:click", ["$event"])
  onDocumentClick(event: MouseEvent): void {
    if (!this.showActions) return;
    const target = event.target as Element | null;
    if (
      target &&
      this.host.nativeElement.contains(target) &&
      target.closest(".ig-card__more, .ig-card__dropdown")
    ) {
      return;
    }
    this.showActions = false;
  }

  @HostListener("document:keydown.escape")
  onEscape(): void {
    this.showActions = false;
  }

  /** Modal local de confirmação de exclusão de comentário/reply (evita duplicação de confirmDialog). */
  deleteConfirmVisible = false;
  deleteInFlight = false;
  private pendingDelete: { kind: 'comment'; target: CommentWithReplies }
                       | { kind: 'reply'; parent: CommentWithReplies; target: SocialCommentResponse }
                       | null = null;

  constructor(
    private social: SocialService,
    private messageService: MessageService,
    private router: Router,
    private translocoService: TranslocoService,
    private imageUrlService: ImageUrlService,
    private host: ElementRef<HTMLElement>
  ) {}

  /** So vira true quando o card chega perto da viewport. Ate la nenhum <video>
   *  recebe src, entao o navegador nao abre conexao nenhuma por ele.
   *
   *  Antes, os 20 posts da pagina entravam no DOM ja com [src], e o navegador
   *  buscava metadado dos 20 ao mesmo tempo, direto do S3. Em mp4 gravado por
   *  celular o atomo `moov` costuma ficar no FIM do arquivo (sem faststart),
   *  entao "so o metadado" pode significar puxar um pedaco grande do video. */
  nearViewport = false;
  private io?: IntersectionObserver;

  /** Slide mais distante que o usuario ja alcancou neste card. A navegacao e
   *  sempre de um em um (botao ou swipe), entao "ja visto" == indice <= este.
   *  Serve para o src ser CUMULATIVO: tirar o src de um video que ficou para
   *  tras cortaria a reproducao, porque o carrossel mantem todos os slides no
   *  DOM (o deslocamento e por translateX, nao por *ngIf). */
  private maxSlideVisto = 0;

  ngAfterViewInit(): void {
    if (typeof IntersectionObserver === 'undefined') {
      this.nearViewport = true;      // navegador sem suporte: comportamento antigo
      return;
    }
    this.io = new IntersectionObserver(
      entries => {
        if (entries.some(e => e.isIntersecting)) {
          this.nearViewport = true;
          this.io?.disconnect();     // uma vez visto, fica carregado
          this.io = undefined;
        }
      },
      { rootMargin: '300px 0px' }    // comeca um pouco antes de entrar na tela
    );
    this.io.observe(this.host.nativeElement);
  }

  /** Timer p/ separar clique simples (play/pause) de double-click (curtir) no vídeo. */
  private videoClickTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Gate do player HLS novo (task-front-brief, ruling #3): vídeo antigo não tem job de
   * transcode e cai em PENDING para sempre pelo contrato — por isso o rollout é por flag, não
   * automático. Com a flag desligada, `*ngIf="hlsPlaybackEnabled; else legacyVideo"` no
   * template garante que o `<video>` MP4 atual segue exatamente como está hoje e
   * app-hls-video-player nem chega a ser criado.
   */
  get hlsPlaybackEnabled(): boolean {
    return enviroment.HLS_PLAYBACK;
  }

  /**
   * Sinal de ativação do player HLS novo — o MESMO gate que `videoSrc()` usa pro `<video>` MP4
   * legado (não um mecanismo novo, ver review B1): só card perto da tela E slide já alcançado no
   * carrossel. `HlsVideoPlayerComponent` ainda soma o próprio IntersectionObserver por cima
   * antes de disparar a sessão — este método só garante que o card passa o sinal correto.
   */
  videoActive(index: number): boolean {
    return this.nearViewport && index <= this.maxSlideVisto;
  }

  /** Clique simples na camada do vídeo → play/pause (com atraso p/ não conflitar com o double-click). */
  onVideoTap(video: TappableVideo): void {
    if (this.videoClickTimer) {
      clearTimeout(this.videoClickTimer);
    }
    this.videoClickTimer = setTimeout(() => {
      this.videoClickTimer = null;
      if (video.paused) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    }, 250);
  }

  /** Double-click na camada do vídeo → curtir (sem play/pause e sem o fullscreen nativo). */
  onVideoDblTap(): void {
    if (this.videoClickTimer) {
      clearTimeout(this.videoClickTimer);
      this.videoClickTimer = null;
    }
    this.likeFromDoubleTap();
  }

  ngOnDestroy(): void {
    this.io?.disconnect();
    if (this.videoClickTimer) {
      clearTimeout(this.videoClickTimer);
    }
    if (this.heartTimer) {
      clearTimeout(this.heartTimer);
      this.heartTimer = null;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['post'] && this.post) {
      this.likedLocal = this.post.isLikedByViewer ?? false;
      this.currentSlide = 0;
      this.maxSlideVisto = 0;
      this.showHeart = false;
      this.pendingDoubleTapLike = false;
      this.lastLikeBurst = 0;
      // Razão inicial já padronizada (evita "card esticado" antes de o load refinar a orientação).
      const isVideo = this.sortedMedia()[0]?.mediaType === 'Video';
      this.mediaAspectRatio = resolveMediaAspectRatio(0, 0, isVideo, this.context);
    }
  }

  onFirstMediaImageLoad(event: Event): void {
    const img = event.target as HTMLImageElement;
    this.mediaAspectRatio = resolveMediaAspectRatio(
      img.naturalWidth,
      img.naturalHeight,
      false,
      this.context
    );
  }

  onFirstMediaVideoMeta(event: Event): void {
    const video = event.target as HTMLVideoElement;
    this.mediaAspectRatio = resolveMediaAspectRatio(
      video.videoWidth,
      video.videoHeight,
      true,
      this.context
    );
  }

  get isOwner(): boolean {
    return !!this.currentUserId && this.post.authorUserId === this.currentUserId;
  }

  get authorDisplay(): string {
    return this.post.authorNickname
      || this.post.authorName
      || this.post.authorUserId?.substring(0, 8) + "...";
  }

  get authorInitial(): string {
    const src = this.post.authorNickname || this.post.authorName || this.post.authorUserId || "?";
    return src.charAt(0).toUpperCase();
  }

  get effectiveAvatarUrl(): string | null {
    const raw = this.post.authorAvatarUrl
      || (this.post.authorUserId === this.currentUserId ? this.currentUserAvatarUrl : null)
      || null;
    return this.imageUrlService.transformSocialUrl(raw, 'avatar');
  }

  /** Fonte do video: so o slide ATIVO de um card que ja chegou perto da tela.
   *  Devolve null para o resto — com [attr.src]="null" o atributo nem existe no
   *  elemento, entao o navegador nao busca nada. */
  videoSrc(media: { url: string }, index: number): string | null {
    if (!this.nearViewport || index > this.maxSlideVisto) return null;
    return media.url;
  }

  /** Capa do video. So aceita miniatura de verdade: no upload o front grava
   *  `thumbnailUrl` = a propria URL do video como placeholder, e apontar um
   *  <img>/poster para um .mp4 nao renderiza nada. */
  videoPoster(media: { url: string; thumbnailUrl?: string | null }): string | null {
    const thumb = media.thumbnailUrl;
    if (!thumb || thumb === media.url) return null;
    if (/\.(mp4|mov|webm|m4v)(\?|$)/i.test(thumb)) return null;
    return this.imageUrlService.transformSocialUrl(thumb, 'social');
  }

  mediaUrl(url: string, mediaType: string): string {
    if (mediaType === 'Video') return url;
    return this.imageUrlService.transformSocialUrl(url, 'social') ?? url;
  }

  /** Avatar exibido como ator principal: projeto, com fallback para o autor. */
  get primaryAvatarUrl(): string | null {
    return this.post.projectAvatarUrl || this.effectiveAvatarUrl;
  }

  /** Inicial do projeto para fallback do avatar. */
  get primaryInitial(): string {
    const src = this.post.projectName || this.post.authorNickname || this.post.authorName || this.post.authorUserId || '?';
    return src.charAt(0).toUpperCase();
  }

  /** Nome principal exibido no header (projeto). */
  get primaryDisplay(): string {
    return this.post.projectName || this.authorDisplay;
  }

  /** Tem projeto associado? Em posts legados pode ser null. */
  get hasProject(): boolean {
    return !!this.post.projectId && !!this.post.projectName;
  }

  /**
   * Viewer segue o projeto do post. A fonte é o próprio post (`viewerFollowsProject`, calculado
   * pela API para cada página); `followedProjectIds` cobre o que a tela souber além disso — o
   * perfil do projeto, por exemplo, quando o viewer acabou de seguir pelo botão do cabeçalho.
   */
  get isFollowingProject(): boolean {
    return !!this.post.viewerFollowsProject
      || (!!this.post.projectId && this.followedProjectIds.has(this.post.projectId));
  }

  /** Mostra o CTA "Seguir": apenas quando há projeto, viewer não é dono e ainda não segue. */
  get canFollowProject(): boolean {
    return this.hasProject && !this.isOwner && !!this.post.projectId && !this.isFollowingProject;
  }

  goProjectProfile(): void {
    if (this.post.projectId) {
      this.router.navigate(['/feed/project', this.post.projectId]);
    }
  }

  followProjectFromCard(): void {
    const projectId = this.post.projectId;
    if (!projectId || this.followProjectBusy) return;
    this.followProjectBusy = true;
    this.social.followProject(projectId).subscribe({
      next: () => {
        this.followProjectBusy = false;
        this.post.viewerFollowsProject = true;
        this.projectFollowed.emit(projectId);
      },
      error: (err) => {
        this.followProjectBusy = false;
        this.messageService.add({
          severity: "error",
          summary: this.translocoService.translate('socialFeed.follow'),
          detail: SocialService.parseHttpErrorMessage(err),
        });
      },
    });
  }

  commentAuthorDisplay(c: SocialCommentResponse): string {
    return c.authorNickname
      || c.authorName
      || c.authorUserId?.substring(0, 8) + "…";
  }

  sortedMedia() {
    return [...(this.post.media || [])].sort((a, b) => a.sortOrder - b.sortOrder);
  }

  prevSlide(): void {
    this.currentSlide = Math.max(0, this.currentSlide - 1);
  }

  nextSlide(): void {
    this.currentSlide = Math.min(this.sortedMedia().length - 1, this.currentSlide + 1);
    this.maxSlideVisto = Math.max(this.maxSlideVisto, this.currentSlide);
  }

  onTouchStart(e: TouchEvent): void {
    this.touchStartX = e.changedTouches[0].clientX;
    this.touchStartY = e.changedTouches[0].clientY;
  }

  onTouchEnd(e: TouchEvent): void {
    const dx = e.changedTouches[0].clientX - this.touchStartX;
    const dy = e.changedTouches[0].clientY - this.touchStartY;
    // Toque curto (sem arrastar) → detecta double-tap para curtir (estilo Instagram).
    if (Math.abs(dx) < 10 && Math.abs(dy) < 10) {
      const now = Date.now();
      if (now - this.lastTapTime < 300) {
        this.likeFromDoubleTap();
        this.lastTapTime = 0;
      } else {
        this.lastTapTime = now;
      }
      return;
    }
    if (Math.abs(dx) < 40 || Math.abs(dy) > Math.abs(dx)) return;
    if (dx < 0) {
      this.nextSlide();
    } else {
      this.prevSlide();
    }
  }

  /**
   * Curtir por double-tap/click na imagem (estilo Instagram): sempre mostra a
   * animação do coração; curte apenas se ainda não estava curtido — nunca
   * descurte por double-tap.
   */
  likeFromDoubleTap(): void {
    // Um único double-tap dispara o `touchend` (nossa detecção) E o `dblclick`
    // sintético do navegador; toques repetidos muito rápidos também. Coalescemos
    // tudo numa curtida/animação só.
    const now = Date.now();
    if (now - this.lastLikeBurst < 500) return;
    this.lastLikeBurst = now;
    this.triggerHeartBurst();
    // Double-tap sempre CURTE (nunca descurte). Se já curtido, só anima.
    if (this.likedLocal) return;
    if (this.likeBusy) {
      // Uma curtida/descurtida está em voo (ex.: acabou de descurtir no botão):
      // aplica o like assim que ela terminar, para não perder o primeiro double-tap.
      this.pendingDoubleTapLike = true;
    } else {
      this.toggleLike();
    }
  }

  private triggerHeartBurst(): void {
    // Só liga o coração (sem desligar/religar): setar `true` sobre `true` não
    // recria o elemento, então não há flicker. O timer único desliga ao final.
    if (this.heartTimer) {
      clearTimeout(this.heartTimer);
    }
    this.showHeart = true;
    this.heartTimer = setTimeout(() => {
      this.showHeart = false;
      this.heartTimer = null;
    }, 800);
  }

  /** Aplica o like adiado (double-tap durante uma operação em voo), se ainda fizer sentido. */
  private applyPendingDoubleTapLike(): void {
    if (!this.pendingDoubleTapLike) {
      return;
    }
    this.pendingDoubleTapLike = false;
    if (!this.likedLocal && !this.likeBusy) {
      this.toggleLike();
    }
  }

  toggleLike(): void {
    if (this.likeBusy) return;
    this.likeBusy = true;

    const wasLiked = this.likedLocal;
    this.likedLocal = !wasLiked;
    this.post.likesCount = Math.max(0, this.post.likesCount + (wasLiked ? -1 : 1));

    const op = wasLiked
      ? this.social.unlikePost(this.post.id)
      : this.social.likePost(this.post.id);

    op.subscribe({
      next: () => {
        this.likeBusy = false;
        this.applyPendingDoubleTapLike();
      },
      error: (err) => {
        this.likedLocal = wasLiked;
        this.post.likesCount = Math.max(0, this.post.likesCount + (wasLiked ? 1 : -1));
        this.likeBusy = false;
        this.applyPendingDoubleTapLike();
        this.messageService.add({
          severity: "error",
          summary: this.translocoService.translate('socialFeed.error'),
          detail: SocialService.parseHttpErrorMessage(err),
        });
      },
    });
  }

  toggleComments(): void {
    this.showComments = !this.showComments;
    if (this.showComments && !this.comments.length) {
      this.fetchComments();
    }
  }

  fetchComments(): void {
    this.commentsLoading = true;
    this.social.getComments(this.post.id).subscribe({
      next: (list) => {
        this.comments = list.map((c) => this.wrapComment(c));
        this.commentsLoading = false;
      },
      error: (err) => {
        this.commentsLoading = false;
        this.messageService.add({
          severity: "error",
          summary: this.translocoService.translate('socialFeed.commentsLabel'),
          detail: SocialService.parseHttpErrorMessage(err),
        });
      },
    });
  }

  /** Envelopa comentário cru com estado de UI. */
  private wrapComment(c: SocialCommentResponse, replies: SocialCommentResponse[] = []): CommentWithReplies {
    return {
      ...c,
      replies,
      repliesLoaded: false,
      repliesLoading: false,
      repliesExpanded: false,
      isReplying: false,
      replyBody: '',
      replySending: false,
    };
  }

  sendComment(): void {
    const body = this.newComment?.trim();
    if (!body || this.commentSending) return;
    this.commentSending = true;
    this.social.createComment(this.post.id, { body }).subscribe({
      next: (created) => {
        // Prepend localmente — lista vem em ordem decrescente
        const wrapped = this.wrapComment(created);
        wrapped.repliesLoaded = true; // comentário novo não tem replies
        this.comments = [wrapped, ...this.comments];
        this.newComment = "";
        this.post.commentsCount += 1;
        this.commentSending = false;
      },
      error: (err) => {
        this.commentSending = false;
        this.messageService.add({
          severity: "error",
          summary: this.translocoService.translate('socialFeed.commentLabel'),
          detail: SocialService.parseHttpErrorMessage(err),
        });
      },
    });
  }

  /** Lazy-load + toggle das replies do comentário. */
  toggleReplies(c: CommentWithReplies): void {
    if (!c.repliesLoaded) {
      c.repliesLoading = true;
      this.social.listReplies(c.id).subscribe({
        next: (replies) => {
          c.replies = replies;
          c.repliesLoaded = true;
          c.repliesExpanded = true;
          c.repliesLoading = false;
        },
        error: (err) => {
          c.repliesLoading = false;
          this.messageService.add({
            severity: "error",
            summary: this.translocoService.translate('socialFeed.commentsLabel'),
            detail: SocialService.parseHttpErrorMessage(err),
          });
        },
      });
      return;
    }
    c.repliesExpanded = !c.repliesExpanded;
  }

  /** Mostra/oculta input de reply. Quando responde a uma reply, prefixa @nickname no body. */
  toggleReplyInput(parent: CommentWithReplies, replyToNickname?: string | null): void {
    if (parent.isReplying) {
      parent.isReplying = false;
      parent.replyBody = '';
      return;
    }
    parent.isReplying = true;
    parent.replyBody = replyToNickname ? `@${replyToNickname} ` : '';
  }

  sendReply(parent: CommentWithReplies): void {
    const body = parent.replyBody?.trim();
    if (!body || parent.replySending) return;
    parent.replySending = true;
    this.social.replyToComment(parent.id, { body }).subscribe({
      next: (reply) => {
        // Replies vêm em ordem crescente — append no fim
        parent.replies = [...parent.replies, reply];
        parent.repliesLoaded = true;
        parent.repliesExpanded = true;
        parent.isReplying = false;
        parent.replyBody = '';
        parent.replySending = false;
        this.post.commentsCount += 1;
      },
      error: (err) => {
        parent.replySending = false;
        this.messageService.add({
          severity: "error",
          summary: this.translocoService.translate('socialFeed.commentLabel'),
          detail: SocialService.parseHttpErrorMessage(err),
        });
      },
    });
  }

  /** Visível para autor do comentário OU dono do post. Backend valida (403 se mentir). */
  canDeleteComment(c: SocialCommentResponse): boolean {
    if (!this.currentUserId) return false;
    return c.authorUserId === this.currentUserId || this.post.authorUserId === this.currentUserId;
  }

  deleteTopLevelComment(c: CommentWithReplies): void {
    this.pendingDelete = { kind: 'comment', target: c };
    this.deleteConfirmVisible = true;
  }

  deleteReply(parent: CommentWithReplies, reply: SocialCommentResponse): void {
    this.pendingDelete = { kind: 'reply', parent, target: reply };
    this.deleteConfirmVisible = true;
  }

  cancelDelete(): void {
    if (this.deleteInFlight) return;
    this.deleteConfirmVisible = false;
    this.pendingDelete = null;
  }

  confirmDelete(): void {
    if (!this.pendingDelete || this.deleteInFlight) return;
    const ctx = this.pendingDelete;
    this.deleteInFlight = true;
    this.social.deleteComment(ctx.target.id).subscribe({
      next: () => {
        if (ctx.kind === 'comment') {
          // Backend decrementa commentsCount em 1 + replies (cascade no soft-delete).
          const removed = 1 + ctx.target.replies.length;
          this.comments = this.comments.filter((x) => x.id !== ctx.target.id);
          this.post.commentsCount = Math.max(0, this.post.commentsCount - removed);
        } else {
          ctx.parent.replies = ctx.parent.replies.filter((r) => r.id !== ctx.target.id);
          this.post.commentsCount = Math.max(0, this.post.commentsCount - 1);
        }
        this.deleteInFlight = false;
        this.deleteConfirmVisible = false;
        this.pendingDelete = null;
        this.messageService.add({
          severity: "success",
          summary: this.translocoService.translate('socialFeed.commentDeleted'),
        });
      },
      error: (err) => {
        this.deleteInFlight = false;
        this.deleteConfirmVisible = false;
        this.pendingDelete = null;
        this.handleDeleteCommentError(err);
      },
    });
  }

  private handleDeleteCommentError(err: any): void {
    const isForbidden = err?.status === 403;
    this.messageService.add({
      severity: "error",
      summary: this.translocoService.translate('socialFeed.commentLabel'),
      detail: this.translocoService.translate(
        isForbidden ? 'socialFeed.deleteForbidden' : 'socialFeed.commentDeleteError'
      ),
    });
  }

  goAuthorProfile(): void {
    this.router.navigate(["/feed/profile", this.post.authorUserId]);
  }

  reportClick(): void {
    this.reportPost.emit(this.post.id);
  }

  /** Denunciar comentário disponível apenas para quem não é o autor do comentário. */
  canReportComment(c: SocialCommentResponse): boolean {
    return !!this.currentUserId && c.authorUserId !== this.currentUserId;
  }

  reportCommentClick(c: SocialCommentResponse): void {
    this.reportComment.emit(c.id);
  }

  reportUserClick(c: SocialCommentResponse): void {
    this.reportUser.emit(c.authorUserId);
  }

  archive(): void {
    this.social.archivePost(this.post.id).subscribe({
      next: () => {
        this.messageService.add({ severity: "success", summary: this.translocoService.translate('socialFeed.postArchived') });
        this.postChanged.emit();
      },
      error: (err) =>
        this.messageService.add({
          severity: "error",
          summary: this.translocoService.translate('socialFeed.error'),
          detail: SocialService.parseHttpErrorMessage(err),
        }),
    });
  }

  unarchive(): void {
    this.social.unarchivePost(this.post.id).subscribe({
      next: () => {
        this.messageService.add({ severity: "success", summary: this.translocoService.translate('socialFeed.postRestored') });
        this.postChanged.emit();
      },
      error: (err) =>
        this.messageService.add({
          severity: "error",
          summary: this.translocoService.translate('socialFeed.error'),
          detail: SocialService.parseHttpErrorMessage(err),
        }),
    });
  }

  deletePost(): void {
    this.social.deletePost(this.post.id).subscribe({
      next: () => {
        this.messageService.add({ severity: "success", summary: this.translocoService.translate('socialFeed.postDeleted') });
        this.postChanged.emit();
      },
      error: (err) =>
        this.messageService.add({
          severity: "error",
          summary: this.translocoService.translate('socialFeed.error'),
          detail: SocialService.parseHttpErrorMessage(err),
        }),
    });
  }
}
